import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CaretRight, MagnifyingGlass, Gear } from '@phosphor-icons/react';
import { useSupportingTools } from '../SupportingToolsContext';
import { MODULES } from '../moduleRegistry';
import { useAllowedScreens, useCanManageSettings } from '../utils/screenAccess';

// The Supporting Tools screen picker, shared by the client workspace and the
// all-screens preview so the two-level navigation only exists once.
//
// Screens are reached through their type: this lists the types first and opens a
// type's screens only once that type is picked. Searching is the one way past
// that gate and deliberately spans every type — looking a screen up by name
// should not require knowing which type it was filed under — so each result
// carries the type it belongs to.
const ROW_BASE = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  borderRadius: '8px',
  textAlign: 'left',
  cursor: 'pointer',
  width: '100%',
};

const LABEL_STYLE = { fontSize: '13px', fontWeight: '600', color: '#111827' };
const DESC_STYLE = { fontSize: '11px', color: '#9CA3AF' };
const HEADING_STYLE = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

function NavRow({
  icon: Icon, label, desc, tag, active, onClick, testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...ROW_BASE,
        border: `1px solid ${active ? '#002FA7' : '#E5E7EB'}`,
        background: active ? '#EFF6FF' : 'white',
      }}
      data-testid={testId}
    >
      <Icon size={20} color={active ? '#002FA7' : '#6B7280'} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {tag && (
          <div style={{ fontSize: '10px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {tag}
          </div>
        )}
        <div style={LABEL_STYLE}>{label}</div>
        <div style={DESC_STYLE}>{desc}</div>
      </div>
      <CaretRight size={14} color="#9CA3AF" />
    </button>
  );
}

function ModuleTypeNav({
  heading, selectedKey, onSelect, testIdPrefix, maxHeight = '700px',
}) {
  const [search, setSearch] = useState('');
  const [openTypeKey, setOpenTypeKey] = useState(null);
  // Screens the signed-in user holds the right for. Everything below lists
  // out of these, so a screen without the right is never offered.
  const { screens, types, hasAny, loading } = useAllowedScreens();
  // Settings (moving screens to Supporting Tools Live) is global rather than per
  // client, so it opens its own page, for super_admin or anyone granted the right.
  const canManageSettings = useCanManageSettings();
  const { variant, basePath } = useSupportingTools();
  const navigate = useNavigate();
  const showSettings = variant === 'standard' && canManageSettings;

  // The preview page takes its selection from the URL, so the open type follows
  // whichever screen is selected rather than only what was clicked in here.
  useEffect(() => {
    const selected = MODULES.find((m) => m.key === selectedKey);
    if (selected) setOpenTypeKey(selected.type);
  }, [selectedKey]);

  const term = search.trim().toLowerCase();
  const results = useMemo(
    () => (term ? screens.filter((m) => m.label.toLowerCase().includes(term)) : null),
    [term, screens],
  );

  // A search overrides the open type, so its results are never hidden behind one.
  const openType = results ? null : types.find((t) => t.key === openTypeKey) || null;
  const typeLabelOf = (key) => (types.find((t) => t.key === key) || {}).label;

  return (
    <div>
      {openType ? (
        <button
          type="button"
          onClick={() => setOpenTypeKey(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent',
            border: 'none', color: '#002FA7', fontSize: '12px', fontWeight: '600',
            cursor: 'pointer', padding: 0, marginBottom: '10px',
          }}
          data-testid={`${testIdPrefix}-back-to-types`}
        >
          <ArrowLeft size={13} /> All Types
        </button>
      ) : (
        <div style={{ ...HEADING_STYLE, marginBottom: '10px' }}>{heading}</div>
      )}

      {openType && (
        <div style={{ marginBottom: '10px' }} data-testid={`${testIdPrefix}-open-type`}>
          <div style={LABEL_STYLE}>{openType.label}</div>
          <div style={DESC_STYLE}>
            {openType.modules.length} {openType.modules.length === 1 ? 'screen' : 'screens'}
          </div>
        </div>
      )}

      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <MagnifyingGlass size={14} color="#9CA3AF" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
        <input
          className="input-field"
          style={{ fontSize: '13px', paddingLeft: '30px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search screens..."
          data-testid={`${testIdPrefix}-search-input`}
        />
      </div>

      {!hasAny && !loading && !showSettings && (
        <div style={{ padding: '12px', fontSize: '12px', color: '#9CA3AF', border: '1px dashed #E5E7EB', borderRadius: '8px' }} data-testid={`${testIdPrefix}-no-access`}>
          You do not have access to any {variant === 'live' ? 'Supporting Tools Live' : 'Supporting Tools'} screen. Ask an administrator to grant the screens you need.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          maxHeight,
          overflowY: 'auto',
          paddingRight: '4px',
          marginRight: '-4px',
        }}
        data-testid={`${testIdPrefix}-list`}
      >
        {results && results.length === 0 && (
          <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF' }} data-testid={`${testIdPrefix}-empty`}>
            No screens match &quot;{search}&quot;.
          </div>
        )}

        {results && results.map((m) => (
          <NavRow
            key={m.key}
            icon={m.icon}
            tag={typeLabelOf(m.type)}
            label={m.label}
            desc={m.desc}
            active={selectedKey === m.key}
            onClick={() => onSelect(m.key)}
            testId={`${testIdPrefix}-option-${m.key}`}
          />
        ))}

        {!results && !openType && types.map((t) => (
          <NavRow
            key={t.key}
            icon={t.icon}
            label={t.label}
            desc={t.desc}
            active={false}
            onClick={() => setOpenTypeKey(t.key)}
            testId={`${testIdPrefix}-type-${t.key}`}
          />
        ))}

        {!results && !openType && showSettings && (
          <NavRow
            icon={Gear}
            label="Settings"
            desc="Move ready screens to Supporting Tools Live"
            active={false}
            onClick={() => navigate(`${basePath}/settings`)}
            testId={`${testIdPrefix}-settings`}
          />
        )}

        {!results && openType && openType.modules.map((m) => (
          <NavRow
            key={m.key}
            icon={m.icon}
            label={m.label}
            desc={m.desc}
            active={selectedKey === m.key}
            onClick={() => onSelect(m.key)}
            testId={`${testIdPrefix}-option-${m.key}`}
          />
        ))}
      </div>
    </div>
  );
}

export default ModuleTypeNav;
